import React, { Component } from "react";
import Breadcrumb from "../common/breadcrumb";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ClipLoader from "react-spinners/ClipLoader";
import {
  getRefundRequests,
  getAllPendingRefundRequests,
  updateRefundStatus,
} from "../../firebase/firebase.utils";

const TABS = ["pending", "approved", "rejected"];

const TAB_STYLE = {
  pending:  { active: "#F59E0B", bg: "#FFFBEB", badge: "#F59E0B" },
  approved: { active: "#059669", bg: "#ECFDF5", badge: "#059669" },
  rejected: { active: "#DC2626", bg: "#FEF2F2", badge: "#DC2626" },
};

const STATUS_BADGE = {
  pending:  { bg: "#FEF3C7", color: "#92400E" },
  approved: { bg: "#D1FAE5", color: "#065F46" },
  rejected: { bg: "#FEE2E2", color: "#991B1B" },
};

const MODAL_CONFIG = {
  approve: {
    title: "Approve Refund",
    headerGradient: "linear-gradient(135deg, #059669, #10B981)",
    confirmLabel: "Yes, Approve",
    confirmGradient: "linear-gradient(135deg, #059669, #10B981)",
    icon: "✓",
    iconBg: "#D1FAE5",
    iconColor: "#059669",
    description: "This refund request will be marked as approved.",
  },
  reject: {
    title: "Reject Refund",
    headerGradient: "linear-gradient(135deg, #DC2626, #EF4444)",
    confirmLabel: "Yes, Reject",
    confirmGradient: "linear-gradient(135deg, #DC2626, #EF4444)",
    icon: "✕",
    iconBg: "#FEE2E2",
    iconColor: "#DC2626",
    description: "This refund request will be marked as rejected.",
  },
};

class RefundRequests extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "pending",
      pending:  { docs: [], loading: true,  lastCreatedAt: null, hasMore: false },
      approved: { docs: [], loading: false, lastCreatedAt: null, hasMore: false },
      rejected: { docs: [], loading: false, lastCreatedAt: null, hasMore: false },
      loadingMore: false,
      updatingId: null,
      // modal
      modalOpen: false,
      modalAction: null,
      modalDoc: null,
      adminNote: "",
      refundedAmount: "",
    };
  }

  async componentDidMount() {
    await this.loadPending();
  }

  loadPending = async () => {
    this.setState((s) => ({ pending: { ...s.pending, loading: true } }));
    const docs = await getAllPendingRefundRequests();
    this.setState({ pending: { docs, loading: false, lastCreatedAt: null, hasMore: false } });
  };

  loadTab = async (tab, reset = false) => {
    if (tab === "pending") { await this.loadPending(); return; }
    const current = this.state[tab];
    const cursor = reset ? null : current.lastCreatedAt;
    if (reset) {
      this.setState((s) => ({ [tab]: { ...s[tab], loading: true, docs: [] } }));
    } else {
      this.setState({ loadingMore: true });
    }
    const result = await getRefundRequests(tab, cursor);
    this.setState((s) => ({
      loadingMore: false,
      [tab]: {
        loading: false,
        docs: reset ? result.docs : [...s[tab].docs, ...result.docs],
        lastCreatedAt: result.lastCreatedAt,
        hasMore: result.hasMore,
      },
    }));
  };

  switchTab = async (tab) => {
    if (tab === this.state.activeTab) return;
    this.setState({ activeTab: tab });
    const tabState = this.state[tab];
    if (tabState.docs.length === 0 && !tabState.loading) {
      await this.loadTab(tab, true);
    }
  };

  openModal = (action, doc) => {
    this.setState({ modalOpen: true, modalAction: action, modalDoc: doc, adminNote: "", refundedAmount: "" });
  };

  closeModal = () => {
    if (this.state.updatingId) return;
    this.setState({ modalOpen: false, modalAction: null, modalDoc: null, adminNote: "", refundedAmount: "" });
  };

  confirmAction = async () => {
    const { modalAction, modalDoc, adminNote, refundedAmount } = this.state;
    if (!modalDoc) return;
    if (modalAction === "approve") {
      const amt = parseFloat(refundedAmount);
      if (!refundedAmount || isNaN(amt) || amt <= 0) {
        toast.error("Please enter the refunded amount.");
        return;
      }
    }
    this.setState({ updatingId: modalDoc.id });
    try {
      const amt = modalAction === "approve" ? parseFloat(refundedAmount) : null;
      await updateRefundStatus(modalDoc.id, modalAction, adminNote.trim(), amt, modalDoc.orderId || null);
      toast.success(modalAction === "approve" ? "Refund approved." : "Refund rejected.");
      this.setState({ modalOpen: false, modalAction: null, modalDoc: null, adminNote: "" });
      await this.loadPending();
      const otherTab = modalAction === "approve" ? "approved" : "rejected";
      this.setState((s) => ({
        [otherTab]: { ...s[otherTab], docs: [], lastCreatedAt: null, hasMore: false },
      }));
    } catch (e) {
      toast.error("Failed: " + e.message);
    }
    this.setState({ updatingId: null });
  };

  formatDate = (ts) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("en-BD", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  renderModal() {
    const { modalOpen, modalAction, modalDoc, adminNote, refundedAmount, updatingId } = this.state;
    if (!modalOpen || !modalDoc) return null;
    const cfg = MODAL_CONFIG[modalAction];
    const isRejection = modalAction === "reject";
    const isLoading = updatingId === modalDoc.id;

    return (
      <div
        onClick={this.closeModal}
        style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(15,10,42,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "20px",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: 20,
            width: "100%",
            maxWidth: 460,
            boxShadow: "0 24px 64px rgba(15,10,42,0.28)",
            overflow: "hidden",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {/* Header */}
          <div style={{ background: cfg.headerGradient, padding: "22px 28px", position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, color: "#fff", fontWeight: 700, flexShrink: 0,
              }}>
                {cfg.icon}
              </div>
              <div>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
                  {cfg.title}
                </div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, marginTop: 3 }}>
                  {cfg.description}
                </div>
              </div>
            </div>
            <button
              onClick={this.closeModal}
              disabled={isLoading}
              style={{
                position: "absolute", top: 16, right: 16,
                background: "rgba(255,255,255,0.18)", border: "none",
                borderRadius: 8, width: 32, height: 32,
                color: "#fff", fontSize: 18, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>

          {/* Order summary */}
          <div style={{ padding: "20px 28px 0" }}>
            <div style={{
              background: "#F8FAFC",
              border: "1px solid #EDE9FE",
              borderRadius: 12,
              padding: "14px 18px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#6D28D9", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
                Refund Details
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>Order ID</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#4F46E5" }}>
                    #{modalDoc.orderId || modalDoc.id}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>Amount Paid</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#059669" }}>
                    {modalDoc.orderTotalPaid != null ? `৳ ${modalDoc.orderTotalPaid}` : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>Requested</div>
                  <div style={{ fontSize: 12, color: "#374151" }}>
                    {this.formatDate(modalDoc.createdAt)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>Current Status</div>
                  <div>
                    <span style={{
                      background: STATUS_BADGE[modalDoc.refundStatus]?.bg || "#F1F5F9",
                      color: STATUS_BADGE[modalDoc.refundStatus]?.color || "#374151",
                      borderRadius: 5, padding: "2px 8px",
                      fontSize: 11, fontWeight: 700, textTransform: "capitalize",
                    }}>
                      {modalDoc.refundStatus}
                    </span>
                  </div>
                </div>
              </div>
              {modalDoc.refundNote && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Customer Reason</div>
                  <div style={{
                    background: "#FFFBEB", border: "1px solid #FCD34D",
                    borderLeft: "3px solid #F59E0B", borderRadius: 6,
                    padding: "7px 11px", fontSize: 12, color: "#78350F", lineHeight: 1.5,
                  }}>
                    {modalDoc.refundNote}
                  </div>
                </div>
              )}
            </div>

            {/* Approval: refunded amount input */}
            {!isRejection && (
              <div style={{ marginTop: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>
                  Refunded Amount <span style={{ color: "#DC2626" }}>*</span>
                  {modalDoc.orderTotalPaid != null && (
                    <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>
                      (paid: ৳{modalDoc.orderTotalPaid})
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  min={0}
                  max={modalDoc.orderTotalPaid || undefined}
                  placeholder="Enter amount to refund…"
                  value={refundedAmount}
                  onChange={(e) => this.setState({ refundedAmount: e.target.value })}
                  disabled={isLoading}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    border: "1.5px solid #E2E8F0", borderRadius: 10,
                    padding: "10px 14px", fontSize: 14, color: "#1E293B",
                    fontFamily: "inherit", outline: "none",
                    transition: "border-color 0.15s",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "#059669"; }}
                  onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; }}
                />
              </div>
            )}

            {/* Rejection note textarea */}
            {isRejection && (
              <div style={{ marginTop: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>
                  Admin Note <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span>
                </label>
                <textarea
                  rows={3}
                  placeholder="Reason for rejection…"
                  value={adminNote}
                  onChange={(e) => this.setState({ adminNote: e.target.value })}
                  disabled={isLoading}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    border: "1.5px solid #E2E8F0", borderRadius: 10,
                    padding: "10px 14px", fontSize: 13, color: "#1E293B",
                    fontFamily: "inherit", resize: "vertical",
                    outline: "none", transition: "border-color 0.15s",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "#DC2626"; }}
                  onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; }}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: "20px 28px", display: "flex", gap: 10, justifyContent: "flex-end",
          }}>
            <button
              onClick={this.closeModal}
              disabled={isLoading}
              style={{
                padding: "10px 22px", borderRadius: 10, border: "1.5px solid #E2E8F0",
                background: "#fff", color: "#374151", fontSize: 14, fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.5 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={this.confirmAction}
              disabled={isLoading}
              style={{
                padding: "10px 22px", borderRadius: 10, border: "none",
                background: isLoading ? "#cbd5e1" : cfg.confirmGradient,
                color: "#fff", fontSize: 14, fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 8,
                minWidth: 130, justifyContent: "center",
                boxShadow: isLoading ? "none" : "0 2px 12px rgba(0,0,0,0.18)",
              }}
            >
              {isLoading
                ? <><ClipLoader size={14} color="#fff" loading /> Processing…</>
                : cfg.confirmLabel
              }
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderTable(tab) {
    const { docs, loading, hasMore } = this.state[tab];
    const { loadingMore, updatingId } = this.state;
    const isPending = tab === "pending";

    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <ClipLoader size={28} color="#6D28D9" loading />
          <div style={{ marginTop: 10, color: "#94a3b8", fontSize: 13 }}>Loading…</div>
        </div>
      );
    }

    if (docs.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "56px 0" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>
            {tab === "pending" ? "📬" : tab === "approved" ? "✅" : "❌"}
          </div>
          <div style={{ color: "#64748b", fontSize: 15, fontWeight: 600 }}>
            No {tab} refund requests
          </div>
          <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
            {tab === "pending" ? "All caught up!" : `No ${tab} requests yet.`}
          </div>
        </div>
      );
    }

    return (
      <>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg, #4F46E5, #7C3AED)" }}>
                {["#", "Order ID", "Total Paid", "Customer Reason", "Requested At", "Status",
                  ...(isPending ? ["Actions"] : ["Admin Note"])].map((h) => (
                  <th key={h} style={{
                    color: "#fff", fontWeight: 700, fontSize: 11,
                    padding: "12px 16px", textAlign: "left",
                    whiteSpace: "nowrap", letterSpacing: "0.06em", textTransform: "uppercase",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {docs.map((doc, i) => {
                const badge = STATUS_BADGE[doc.refundStatus] || STATUS_BADGE.pending;
                const isBusy = updatingId === doc.id;
                return (
                  <tr
                    key={doc.id}
                    style={{
                      borderBottom: "1px solid #f1f5f9",
                      background: i % 2 === 0 ? "#fff" : "#FAFBFF",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F0FF"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#FAFBFF"; }}
                  >
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>
                      {i + 1}
                    </td>
                    <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#4F46E5" }}>
                        #{doc.orderId || doc.id}
                      </div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                        ID: {doc.id?.slice(0, 8)}…
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#059669" }}>
                        {doc.orderTotalPaid != null ? `৳ ${doc.orderTotalPaid}` : "—"}
                      </div>
                    </td>
                    <td style={{ padding: "10px 16px", maxWidth: 240 }}>
                      {doc.refundNote ? (
                        <div style={{
                          background: "#FFFBEB", border: "1px solid #FCD34D",
                          borderLeft: "3px solid #F59E0B", borderRadius: 6,
                          padding: "5px 9px", fontSize: 12, color: "#78350F", lineHeight: 1.4,
                        }}>
                          {doc.refundNote}
                        </div>
                      ) : (
                        <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                      {this.formatDate(doc.createdAt)}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{
                        background: badge.bg, color: badge.color,
                        borderRadius: 20, padding: "3px 10px",
                        fontSize: 11, fontWeight: 700, textTransform: "capitalize",
                        display: "inline-block",
                      }}>
                        {doc.refundStatus}
                      </span>
                    </td>

                    {/* Actions column (pending) */}
                    {isPending && (
                      <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                        {isBusy ? (
                          <ClipLoader size={16} color="#6D28D9" loading />
                        ) : (
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              onClick={() => this.openModal("approve", doc)}
                              style={{
                                background: "linear-gradient(135deg, #059669, #10B981)",
                                color: "#fff", border: "none", borderRadius: 8,
                                padding: "6px 14px", fontSize: 12, fontWeight: 600,
                                cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                                boxShadow: "0 1px 6px rgba(5,150,105,0.3)",
                                transition: "transform 0.1s, box-shadow 0.1s",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 3px 10px rgba(5,150,105,0.4)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 6px rgba(5,150,105,0.3)"; }}
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => this.openModal("reject", doc)}
                              style={{
                                background: "linear-gradient(135deg, #DC2626, #EF4444)",
                                color: "#fff", border: "none", borderRadius: 8,
                                padding: "6px 14px", fontSize: 12, fontWeight: 600,
                                cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                                boxShadow: "0 1px 6px rgba(220,38,38,0.3)",
                                transition: "transform 0.1s, box-shadow 0.1s",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 3px 10px rgba(220,38,38,0.4)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 6px rgba(220,38,38,0.3)"; }}
                            >
                              ✕ Reject
                            </button>
                          </div>
                        )}
                      </td>
                    )}

                    {/* Admin note column (approved/rejected) */}
                    {!isPending && (
                      <td style={{ padding: "10px 16px", maxWidth: 200, fontSize: 12, color: "#64748b" }}>
                        {doc.adminNote ? (
                          <div style={{
                            background: "#F1F5F9", borderRadius: 6,
                            padding: "5px 9px", fontSize: 12, color: "#374151", lineHeight: 1.4,
                          }}>
                            {doc.adminNote}
                          </div>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div style={{ textAlign: "center", padding: "18px 0" }}>
            <button
              onClick={() => this.loadTab(tab)}
              disabled={loadingMore}
              style={{
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                color: "#fff", border: "none", borderRadius: 10,
                padding: "9px 28px", fontSize: 13, fontWeight: 600,
                cursor: loadingMore ? "not-allowed" : "pointer",
                boxShadow: "0 2px 10px rgba(79,70,229,0.25)",
                display: "inline-flex", alignItems: "center", gap: 8,
              }}
            >
              {loadingMore ? <><ClipLoader size={14} color="#fff" loading /> Loading…</> : "Load More"}
            </button>
          </div>
        )}
      </>
    );
  }

  render() {
    const { activeTab, pending, approved, rejected } = this.state;
    const counts = {
      pending:  pending.docs.length,
      approved: approved.docs.length,
      rejected: rejected.docs.length,
    };

    return (
      <div style={{ background: "#F8F7FF", minHeight: "100vh" }}>
        <ToastContainer />
        {this.renderModal()}

        <Breadcrumb title="Refund Requests" parent="Home" />
        <div className="container-fluid" style={{ paddingTop: 8, paddingBottom: 32 }}>
          <div className="card" style={{
            borderRadius: 16, boxShadow: "0 4px 24px rgba(79,70,229,0.08)",
            border: "1px solid #EDE9FE", overflow: "hidden",
          }}>

            {/* Tab bar */}
            <div style={{ display: "flex", borderBottom: "2px solid #EDE9FE", background: "#fff" }}>
              {TABS.map((tab) => {
                const style = TAB_STYLE[tab];
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => this.switchTab(tab)}
                    style={{
                      flex: 1, padding: "15px 0", border: "none",
                      background: isActive ? style.bg : "#fff",
                      borderBottom: isActive ? `3px solid ${style.active}` : "3px solid transparent",
                      color: isActive ? style.active : "#64748b",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 14, cursor: "pointer", textTransform: "capitalize",
                      transition: "all 0.15s ease",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    {counts[tab] > 0 && (
                      <span style={{
                        background: style.badge, color: "#fff",
                        borderRadius: 10, fontSize: 11, fontWeight: 700,
                        padding: "2px 8px", minWidth: 22, textAlign: "center",
                        display: "inline-block",
                      }}>
                        {counts[tab]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div style={{ padding: "0 0 8px" }}>
              {this.renderTable(activeTab)}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default RefundRequests;
